import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@ayble-health/dashboard-components';
import type { ProviderVisit } from '../../models/providerHome';
import { VisitRow } from './VisitRow';
import { SUBTLE } from './tokens';

const SLOT_MIN = 30; // half-hour grid
const SLOT_H = 56; // px per half-hour slot

const fmtHour = (h: number) => {
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour} ${period}`;
};

const minutesOf = (iso: string) => {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
};

/** Red "now" marker drawn at the top of the current/next visit's slot. */
const NowLine = () => (
  <Box position="absolute" top="-1px" left="0" right="0" zIndex={1}>
    <Box h="2px" bg="squash" />
    <Box
      position="absolute"
      left="-3px"
      top="-3px"
      w="8px"
      h="8px"
      borderRadius="full"
      bg="squash"
    />
  </Box>
);

export const DayTimeline = ({
  visits,
  showNowLine = false,
}: {
  visits: ProviderVisit[];
  showNowLine?: boolean;
}) => {
  if (!visits.length) {
    return (
      <Text px={3} py={6} color={SUBTLE}>
        No visits scheduled.
      </Text>
    );
  }

  const mins = visits.map((v) => minutesOf(v.time));
  const start = Math.floor(Math.min(...mins) / 60) * 60;
  const end = Math.ceil((Math.max(...mins) + 1) / 60) * 60; // hour after last visit
  const byMin = new Map(visits.map((v) => [minutesOf(v.time), v]));
  const next = visits.find((v) => v.isNext);
  const nextMin = next ? minutesOf(next.time) : null;
  // Use the isNext visit's time as "mock now" so isPast is relative to the
  // fixture's represented point in time, not the user's actual wall clock.
  const mockNow = next ? new Date(next.time) : null;

  const slots: number[] = [];
  for (let m = start; m <= end; m += SLOT_MIN) slots.push(m);

  return (
    <Box pt="8px">
      {slots.map((m) => {
        const hour = Math.floor(m / 60);
        const onHour = m % 60 === 0;
        const visit = byMin.get(m);
        const showNow = showNowLine && nextMin != null && m === nextMin;

        return (
          <Flex key={m} minH={`${SLOT_H}px`} align="stretch">
            <Box w="52px" flexShrink={0} position="relative">
              {onHour ? (
                <Text
                  position="absolute"
                  top="-7px"
                  fontSize="2xs"
                  color={SUBTLE}
                  whiteSpace="nowrap"
                >
                  {fmtHour(hour)}
                </Text>
              ) : null}
            </Box>
            <Box
              flex={1}
              position="relative"
              borderTopWidth={onHour ? '1px' : 0}
              borderColor="cloud"
            >
              {showNow ? <NowLine /> : null}
              {visit ? (
                <Box pt="4px">
                  <VisitRow visit={visit} mockNow={mockNow} />
                </Box>
              ) : null}
            </Box>
          </Flex>
        );
      })}
    </Box>
  );
};
